import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface RealUser {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user';
  status: 'active' | 'suspended';
  joined_at: string;
  api_calls_total: number;
  email_confirmed: boolean;
  last_sign_in: string | null;
}

function formatLastLogin(dateStr: string | null) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function isInactive(dateStr: string | null) {
  if (!dateStr) return true;
  return (Date.now() - new Date(dateStr).getTime()) / 86400000 > 30;
}

function downloadCSV(users: RealUser[]) {
  const headers = ['Username', 'Email', 'Role', 'Status', 'API Calls', 'Email Confirmed', 'Last Login', 'Joined'];
  const rows = users.map(u => [
    u.username,
    u.email,
    u.role,
    u.status,
    u.api_calls_total,
    u.email_confirmed ? 'Yes' : 'No',
    u.last_sign_in ? new Date(u.last_sign_in).toLocaleString() : 'Never',
    u.joined_at ? new Date(u.joined_at).toLocaleDateString() : '—',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function UsersTab() {
  const { user: currentAdmin } = useAuthContext();
  const [users, setUsers] = useState<RealUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [welcomeLoading, setWelcomeLoading] = useState<string | null>(null);

  // Assign product modal
  const [assignModal, setAssignModal] = useState<RealUser | null>(null);
  const [products, setProducts] = useState<{ id: string; name: string; category: string }[]>([]);
  const [assignProductId, setAssignProductId] = useState('');
  const [assignPricingType, setAssignPricingType] = useState<'1_2_year' | '3_5_year' | 'lifetime'>('lifetime');
  const [assignLoading, setAssignLoading] = useState(false);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-users`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.users) setUsers(data.users);
      else setError(data.error || 'Failed to load users');
    } catch {
      setError('Network error loading users');
    }
    setLoading(false);
  }, []);

  const fetchProducts = useCallback(async () => {
    const { data } = await supabase.from('products').select('id, name, category').eq('status', 'active');
    setProducts(data ?? []);
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchProducts();
  }, [fetchUsers, fetchProducts]);

  const callAdminAction = async (body: Record<string, unknown>) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-users`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const toggleUserRole = async (userId: string, currentRole: string) => {
    // Prevent super admin from changing their own role — irreversible foot-gun protection
    if (userId === currentAdmin?.id) {
      showToast('error', 'You cannot change your own admin role. Use Supabase dashboard if needed.');
      return;
    }
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    setActionLoading(userId + '_role');
    const result = await callAdminAction({ action: 'set_role', user_id: userId, role: newRole });
    if (result.success) setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole as 'admin' | 'user' } : u));
    setActionLoading(null);
  };

  const toggleUserBan = async (userId: string, currentStatus: string) => {
    const shouldBan = currentStatus === 'active';
    setActionLoading(userId + '_ban');
    const result = await callAdminAction({ action: 'toggle_ban', user_id: userId, banned: shouldBan });
    if (result.success) setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: shouldBan ? 'suspended' : 'active' } : u));
    setActionLoading(null);
  };

  const filteredUsers = users.filter(u =>
    (userRoleFilter === 'all' || u.role === userRoleFilter) &&
    (!userSearch || u.username?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()))
  );

  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => { const next = new Set(prev); filteredUsers.forEach(u => next.delete(u.id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); filteredUsers.forEach(u => next.add(u.id)); return next; });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const bulkAction = async (action: 'suspend' | 'activate' | 'make_admin' | 'make_user') => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    const ids = Array.from(selectedIds);
    let successCount = 0;
    for (const id of ids) {
      const user = users.find(u => u.id === id);
      if (!user) continue;
      let result;
      if (action === 'suspend' || action === 'activate') {
        result = await callAdminAction({ action: 'toggle_ban', user_id: id, banned: action === 'suspend' });
        if (result.success) {
          setUsers(prev => prev.map(u => u.id === id ? { ...u, status: action === 'suspend' ? 'suspended' : 'active' } : u));
          successCount++;
        }
      } else {
        const role = action === 'make_admin' ? 'admin' : 'user';
        result = await callAdminAction({ action: 'set_role', user_id: id, role });
        if (result.success) {
          setUsers(prev => prev.map(u => u.id === id ? { ...u, role: role as 'admin' | 'user' } : u));
          successCount++;
        }
      }
    }
    setBulkLoading(false);
    setSelectedIds(new Set());
    showToast('success', `Bulk action applied to ${successCount} user${successCount !== 1 ? 's' : ''}`);
  };

  const resendWelcomeEmail = async (user: RealUser) => {
    setWelcomeLoading(user.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/welcome-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_email: user.email, username: user.username }),
      });
      const result = await res.json();
      if (result.success) {
        showToast('success', result.skipped ? 'SMTP not configured yet.' : `Welcome email sent to ${user.email}!`);
      } else {
        showToast('error', result.error || 'Failed to send welcome email');
      }
    } catch {
      showToast('error', 'Network error sending welcome email');
    }
    setWelcomeLoading(null);
  };

  const assignProduct = async () => {
    if (!assignModal || !assignProductId) return;
    setAssignLoading(true);
    const product = products.find(p => p.id === assignProductId);
    if (!product) { setAssignLoading(false); return; }

    const now = new Date();
    let endDate: string | null = null;
    if (assignPricingType === '1_2_year') {
      const d = new Date(now); d.setFullYear(d.getFullYear() + 2); endDate = d.toISOString();
    } else if (assignPricingType === '3_5_year') {
      const d = new Date(now); d.setFullYear(d.getFullYear() + 5); endDate = d.toISOString();
    }

    const { error } = await supabase.from('subscriptions').insert([{
      user_id: assignModal.id,
      user_name: assignModal.username,
      user_email: assignModal.email,
      product_id: assignProductId,
      product_name: product.name,
      pricing_type: assignPricingType,
      start_date: now.toISOString(),
      end_date: endDate,
      status: 'active',
      requests_used: 0,
      requests_limit: 100000,
    }]);

    setAssignLoading(false);
    if (error) {
      showToast('error', `Failed to assign: ${error.message}`);
    } else {
      showToast('success', `${product.name} assigned to ${assignModal.username}`);
      setAssignModal(null);
      setAssignProductId('');
    }
  };

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
          {toast.text}
        </motion.div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" placeholder="Search by name or email…" value={userSearch} onChange={e => setUserSearch(e.target.value)} className="w-full text-sm pl-8 pr-3 py-2 border border-surface-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          <div className="flex items-center gap-1 bg-white border border-surface-3 rounded-lg p-1">
            {(['all', 'admin', 'user'] as const).map(r => (
              <button key={r} onClick={() => setUserRoleFilter(r)} className={`px-3 py-1 text-xs font-medium rounded-md capitalize cursor-pointer whitespace-nowrap transition-colors ${userRoleFilter === r ? 'bg-sidebar text-white' : 'text-gray-500 hover:bg-surface-2'}`}>
                {r === 'all' ? 'All Roles' : r}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{loading ? 'Loading…' : `${filteredUsers.length} of ${users.length} users`}</span>
          <button onClick={() => downloadCSV(filteredUsers)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-surface-3 rounded-lg text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-download-2-line" />CSV
          </button>
          <button onClick={fetchUsers} disabled={loading} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-surface-3 rounded-lg text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-center gap-2 bg-sidebar/5 border border-sidebar/20 rounded-xl px-4 py-3">
          <span className="text-sm font-semibold text-gray-700">{selectedIds.size} selected</span>
          <div className="flex flex-wrap gap-2 ml-2">
            {[
              { label: 'Suspend', action: 'suspend' as const, color: 'border-red-200 text-red-600 hover:bg-red-50' },
              { label: 'Activate', action: 'activate' as const, color: 'border-green-200 text-green-600 hover:bg-green-50' },
              { label: 'Make Admin', action: 'make_admin' as const, color: 'border-brand-200 text-brand-600 hover:bg-brand-50' },
              { label: 'Make User', action: 'make_user' as const, color: 'border-gray-200 text-gray-600 hover:bg-gray-50' },
            ].map(b => (
              <button key={b.action} onClick={() => bulkAction(b.action)} disabled={bulkLoading} className={`text-xs px-3 py-1.5 border rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${b.color}`}>
                {bulkLoading ? <i className="ri-loader-4-line animate-spin" /> : b.label}
              </button>
            ))}
          </div>
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap">
            Clear selection
          </button>
        </motion.div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-red-600">
          <i className="ri-error-warning-line" />{error}
        </div>
      )}

      <div className="bg-white rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 border-b border-surface-3">
              <th className="px-4 py-3 w-8">
                <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} className="rounded border-gray-300 cursor-pointer" />
              </th>
              {['User', 'Role', 'API Calls', 'Status', 'Email', 'Last Login', 'Joined', 'Actions'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-surface-3 animate-pulse">
                  <td className="px-4 py-3"><div className="w-4 h-4 bg-gray-100 rounded" /></td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-gray-100" /><div className="space-y-1"><div className="h-3 bg-gray-100 rounded w-24" /><div className="h-2.5 bg-gray-100 rounded w-32" /></div></div></td>
                  {Array.from({ length: 7 }).map((__, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded" /></td>)}
                </tr>
              ))
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-sm text-gray-400">
                  {userSearch || userRoleFilter !== 'all' ? 'No users match your search' : 'No users found'}
                </td>
              </tr>
            ) : filteredUsers.map((u, i) => (
              <motion.tr key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className={`border-b border-surface-3 last:border-0 hover:bg-surface-2 transition-colors ${selectedIds.has(u.id) ? 'bg-brand-50/30' : ''}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleSelect(u.id)} className="rounded border-gray-300 cursor-pointer" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-sidebar flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {(u.username || u.email || 'U')[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-900">{u.username}</div>
                      <div className="text-[11px] text-gray-400">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {u.id === currentAdmin?.id ? (
                    /* Own account — role badge is locked, not clickable */
                    <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold border whitespace-nowrap select-none ${u.role === 'admin' ? 'bg-brand-50 text-brand-600 border-brand-200' : 'bg-surface-2 text-gray-500 border-surface-3'}`} title="You cannot change your own role">
                      {u.role === 'admin' ? '⭐ ' : ''}{u.role} <i className="ri-lock-line text-[10px] opacity-60" />
                    </span>
                  ) : (
                    <button onClick={() => toggleUserRole(u.id, u.role)} disabled={actionLoading === u.id + '_role'} className={`text-[11px] px-2.5 py-1 rounded-full font-semibold cursor-pointer transition-all border whitespace-nowrap ${u.role === 'admin' ? 'bg-brand-50 text-brand-600 border-brand-200 hover:bg-brand-100' : 'bg-surface-2 text-gray-500 border-surface-3 hover:bg-gray-100'} disabled:opacity-50`}>
                      {actionLoading === u.id + '_role' ? <i className="ri-loader-4-line animate-spin" /> : <>{u.role === 'admin' ? '⭐ ' : ''}{u.role}</>}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 font-mono">{(u.api_calls_total || 0).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${u.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{u.status}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] ${u.email_confirmed ? 'text-green-600' : 'text-amber-500'}`}>
                    <i className={u.email_confirmed ? 'ri-checkbox-circle-line' : 'ri-time-line'} />
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-medium ${isInactive(u.last_sign_in) ? 'text-red-400' : 'text-gray-500'}`} title={u.last_sign_in ? new Date(u.last_sign_in).toLocaleString() : 'Never logged in'}>
                    {isInactive(u.last_sign_in) && <i className="ri-alarm-warning-line mr-1 text-red-400" />}
                    {formatLastLogin(u.last_sign_in)}
                  </span>
                </td>
                <td className="px-4 py-3 text-[11px] text-gray-400">
                  {u.joined_at ? new Date(u.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    <button
                      onClick={() => resendWelcomeEmail(u)}
                      disabled={welcomeLoading === u.id}
                      title="Resend welcome email"
                      className="text-[11px] px-2 py-1 border border-sky-200 text-sky-600 rounded-lg hover:bg-sky-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {welcomeLoading === u.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-mail-line" />}
                    </button>
                    <button onClick={() => { setAssignModal(u); setAssignProductId(products[0]?.id ?? ''); }} className="text-[11px] px-2 py-1 border border-brand-200 text-brand-600 rounded-lg hover:bg-brand-50 cursor-pointer whitespace-nowrap transition-colors" title="Assign product">
                      <i className="ri-gift-line" />
                    </button>
                    <button onClick={() => toggleUserBan(u.id, u.status)} disabled={actionLoading === u.id + '_ban'} className={`text-[11px] px-2 py-1 rounded-lg border cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${u.status === 'active' ? 'border-red-100 text-red-500 hover:bg-red-50' : 'border-green-100 text-green-600 hover:bg-green-50'}`}>
                      {actionLoading === u.id + '_ban' ? <i className="ri-loader-4-line animate-spin" /> : u.status === 'active' ? 'Suspend' : 'Activate'}
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign Product Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-gray-900">Assign Product</h3>
                <p className="text-xs text-gray-400 mt-0.5">Manually grant access to <strong>{assignModal.username}</strong></p>
              </div>
              <button onClick={() => setAssignModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Product</label>
                <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer" value={assignProductId} onChange={e => setAssignProductId(e.target.value)}>
                  {products.length === 0 && <option value="">No active products</option>}
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.category})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Subscription Plan</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: '1_2_year', label: '1-2 Year' },
                    { value: '3_5_year', label: '3-5 Year' },
                    { value: 'lifetime', label: 'Lifetime' },
                  ] as const).map(opt => (
                    <button key={opt.value} onClick={() => setAssignPricingType(opt.value)} className={`py-2 text-xs font-medium rounded-lg border cursor-pointer whitespace-nowrap transition-colors ${assignPricingType === opt.value ? 'bg-brand-500 text-white border-brand-500' : 'border-surface-3 text-gray-600 hover:bg-surface-2'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
                <i className="ri-information-line mr-1" />
                This will create an active subscription immediately without requiring payment.
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setAssignModal(null)} className="flex-1 py-2.5 border border-surface-3 rounded-xl text-sm text-gray-600 cursor-pointer whitespace-nowrap hover:bg-surface-2">Cancel</button>
              <button onClick={assignProduct} disabled={assignLoading || !assignProductId} className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                {assignLoading ? <><i className="ri-loader-4-line animate-spin" />Assigning…</> : <><i className="ri-gift-line" />Assign Product</>}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
